import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Button,
  Table,
  Image,
  Space,
  message,
  Spin,
  Grid,
  Empty,
  List,
  Card,
} from "antd";
import {
  ReloadOutlined,
  DownloadOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

// Reuse token pattern
const getStoredToken = () => {
  const keys = ["token", "accessToken", "AuthToken", "jwtToken"];
  for (const k of keys) {
    const v = localStorage.getItem(k);
    if (v) return v.replace(/^"|"$/g, "");
  }
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.token || user?.accessToken || null;
  } catch {
    return null;
  }
};

const api = axios.create({
  baseURL: global.config.APIURL.BaseURL,
  timeout: 15000,
});
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = {
      ...(config.headers || {}),
      Authorization: `Bearer ${token}`,
    };
  }
  if (config.withCredentials === undefined) config.withCredentials = false;
  return config;
});

const MyDownloads = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const loadData = async (initial = false) => {
    try {
      if (initial) setLoading(true);
      else setFetching(true);

      const res = await api.get("api/MyDownload/GetMyDownloads");
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        messageApi.error("Unauthorized. Please log in again.");
      } else if (err?.request && !err?.response) {
        messageApi.error("Network/CORS error while loading downloads.");
      } else {
        messageApi.error("Failed to load download list.");
      }
    } finally {
      setLoading(false);
      setFetching(false);
    }
  };

  useEffect(() => {
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open a single image URL in a new tab
  const downloadSingle = (url) => {
    if (!url) {
      messageApi.info("No download URL found.");
      return;
    }
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Reusable grid for image previews + per-image download
  const ImagesGrid = ({ record }) => {
    const list = Array.isArray(record?.Images)
      ? record.Images.filter(Boolean)
      : [];
    if (list.length === 0) return <Text type="secondary">No images</Text>;
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(88px, 1fr))",
          gap: 8,
          alignItems: "start",
          width: "100%",
        }}
      >
        {list.map((img, idx) => (
          <div
            key={`${record.ProdID || "row"}_${idx}`}
            style={{ width: "100%" }}
          >
            <Image
              src={img?.DisplayURL || record?.DisplayURL}
              width="100%"
              height={88}
              preview={{ mask: <PictureOutlined /> }}
              style={{
                objectFit: "cover",
                borderRadius: 6,
                border: "1px solid #f0f0f0",
              }}
              fallback={record?.DisplayURL}
            />
            <Button
              block
              size="small"
              type="primary" // changed: theme primary (blue background)
              style={{ marginTop: 6 }} // text will be white automatically
              icon={<DownloadOutlined />}
              onClick={() => downloadSingle(img?.DownloadURLs)}
            >
              Download
            </Button>
          </div>
        ))}
      </div>
    );
  };

  const dataSource = useMemo(() => rows, [rows]);

  const columns = [
    {
      title: "Design No",
      dataIndex: "DesignNo",
      key: "DesignNo",
      width: 140,
      render: (v) => <Text strong>{v || "-"}</Text>,
      ellipsis: true,
      // fixed only for desktop table (mobile uses cards)
    },
    {
      title: "Name",
      dataIndex: "Name",
      key: "Name",
      width: 220,
      ellipsis: true,
      render: (v) => <span style={{ wordBreak: "break-word" }}>{v}</span>,
    },
    {
      title: "Images",
      dataIndex: "Images",
      key: "Images",
      render: (_imgs, record) => <ImagesGrid record={record} />,
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#fff",
        padding: isMobile ? 8 : 12,
        overflowX: "hidden",
      }}
    >
      {contextHolder}

      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <div>
          <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
            My Downloads
          </Title>
          <Text type="secondary" style={{ fontSize: isMobile ? 12 : 13 }}>
            Download product images
          </Text>
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => loadData(false)}
          loading={fetching}
          size={isMobile ? "small" : "middle"}
        >
          Refresh
        </Button>
      </Space>

      {loading ? (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Spin size="large" />
        </div>
      ) : dataSource.length === 0 ? (
        <Empty description="No downloads available" />
      ) : (
        <>
          {isMobile ? (
            <List
              dataSource={dataSource}
              renderItem={(item) => (
                <Card
                  size="small"
                  style={{ marginBottom: 8 }}
                  bodyStyle={{ padding: 10 }}
                >
                  <Space
                    direction="vertical"
                    size={4}
                    style={{ width: "100%" }}
                  >
                    <Text strong>{item?.DesignNo || "-"}</Text>
                    <Text type="secondary" style={{ wordBreak: "break-word" }}>
                      {item?.Name || "-"}
                    </Text>
                    <ImagesGrid record={item} />
                  </Space>
                </Card>
              )}
            />
          ) : (
            <Table
              size="small"
              bordered
              rowKey={(r) => r.ProdID}
              columns={columns}
              dataSource={dataSource}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              scroll={{ x: "max-content" }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default MyDownloads;
